import { logger } from '@/lib/logger';

/**
 * The ticket fields a GUEST last completed a scan-to-pay checkout with, so a returning guest does
 * not have to retype everything at the next QR code they scan.
 *
 * Supersedes `guest-contact-storage.ts` (PB-4851), which held the same idea at narrower scope:
 * three fields, no expiry, and never cleared. Its sign-in invariant is deliberately REVERSED here
 * — see below — because this module treats the stored record as PII to be contained rather than as
 * a convenience to be preserved.
 *
 * ## Invariants:
 * - ONE record per origin, not one per company-and-device (PB-5009, reversing PB-4851). Why: a
 *   guest who pays at Zone A and then scans Zone B a few bays away is the same person with the
 *   same plate, and per-device scoping meant they retyped everything at every code — which is the
 *   whole value of scan-to-pay gone. It also made the touchless page restore nothing at all, since
 *   that page is opened from an email link with no scan behind it, so both ids were undefined and
 *   every read was a silent no-op. Cross-company prefill on a shared origin is the accepted cost,
 *   raised with the requestor; containment is the 90-day expiry plus the sign-in sweep, not the
 *   key shape.
 * - The `pb:qr:guest-fields:` PREFIX is load-bearing and must keep its trailing colon. Why: the
 *   sign-in sweep in `clearAllGuestFields` matches on it, and a key that no longer starts with it
 *   would silently stop being cleared on sign-in — leaving guest PII behind for a signed-in
 *   parker with nothing to notice it by. Records written under the old per-device format are
 *   simply ignored on read and are swept by that same prefix match.
 * - Guests only, enforced by the CALLER. This module is storage; it cannot see auth state. Callers
 *   gate on `isAuthInitialized && !isAuthenticated` — never on `!user`, which is null for a beat on
 *   every cold load even for a signed-in parker (PB-1942 / PB-3053 class).
 * - Written on a SUCCESSFUL submit, never as the parker types. Why: a half-typed plate or an
 *   abandoned form would be handed back as though it had been confirmed.
 * - Fields merge over the existing record rather than replacing it. Why: the plate and the contact
 *   details are captured by different parts of the flow, so a write carrying one must not erase the
 *   other.
 * - Expiry is enforced on READ and the expired entry is removed there. Why: there is no background
 *   sweep in a browser, so the read is the only moment the data can be aged out; leaving it to a
 *   write would keep PII indefinitely on a device nobody returns to.
 * - Cleared on sign-in, across EVERY device. Why: a profile is authoritative, and a parker who used
 *   three zones as a guest has three records — clearing only the one in front of them leaves two to
 *   surface later. This reverses the superseded module's "never cleared, including on sign-in",
 *   which optimised for convenience; PB-4851 makes containment an acceptance criterion.
 * - Filtering a HIDDEN field is the caller's job, at read time, and is never written back. Why: an
 *   operator hiding a field temporarily must not destroy the parker's remembered value, and the
 *   visibility map is not stable across devices.
 * - Every access is guarded and logged on failure. Why: private mode, a full quota and disabled site
 *   data all throw here, and a silent catch makes a prefill that never happens indistinguishable
 *   from a first visit — unreportable from the field.
 */

/** Key prefix for a guest's remembered ticket fields. Public so tests and sweeps agree on it. */
export const GUEST_FIELDS_KEY_PREFIX = 'pb:qr:guest-fields:';

/**
 * The single key every guest record is stored under.
 *
 * Built from the prefix rather than written out, so the sweep and the store cannot drift apart.
 */
export const GUEST_FIELDS_KEY = `${GUEST_FIELDS_KEY_PREFIX}global`;

/** How long a stored record stays usable. Enforced on read. */
export const GUEST_FIELDS_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * The persisted fields, in frontend naming. The backend `TicketFieldRequirements` names differ;
 * `FE_TO_TFR_KEY` in `ticket-field-visibility.ts` is the existing mapping — do not re-declare it.
 */
export interface GuestFieldValues {
  name?: string;
  email?: string;
  phone?: string;
  roomNumber?: string;
  note?: string;
  customerTicket?: string;
  licensePlate?: string;
  state?: string;
  make?: string;
  model?: string;
  color?: string;
  year?: string;
  vin?: string;
}

export interface GuestFieldsClock {
  /** Injected instant, so TTL behaviour is assertable without mutating global time. */
  now?: number;
}

/** Persisted envelope. `savedAt` rides with the values so the TTL needs no second key. */
interface GuestFieldsEnvelope {
  values: GuestFieldValues;
  savedAt: number;
}

const FIELD_KEYS = [
  'name',
  'email',
  'phone',
  'roomNumber',
  'note',
  'customerTicket',
  'licensePlate',
  'state',
  'make',
  'model',
  'color',
  'year',
  'vin',
] as const satisfies readonly (keyof GuestFieldValues)[];

const clean = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/** A record whose every field is empty is the same as no record. */
const hasAnyValue = (values: GuestFieldValues): boolean =>
  FIELD_KEYS.some((key) => Boolean(values[key]));

const isEnvelope = (parsed: unknown): parsed is GuestFieldsEnvelope => {
  if (typeof parsed !== 'object' || parsed === null) return false;
  const candidate = parsed as Record<string, unknown>;
  return (
    typeof candidate.savedAt === 'number' &&
    typeof candidate.values === 'object' &&
    candidate.values !== null
  );
};

/**
 * Reads the stored record, or null when there is nothing usable — absent, unparseable, expired,
 * or present but empty.
 *
 * Never throws: it runs inside an effect on the booking screen, and a parse error must not take the
 * page down.
 */
export function readGuestFields(clock?: GuestFieldsClock): GuestFieldValues | null {
  if (typeof window === 'undefined') return null;

  const key = GUEST_FIELDS_KEY;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
      if (!isEnvelope(parsed)) {
        // Valid JSON but not our shape — a record written by the superseded module, or hand-edited.
        throw new Error('stored guest fields are not a recognised envelope');
      }
    } catch (unreadable) {
      // DISCARD an unreadable record, don't merely refuse it: the AC says the entry is discarded,
      // and without the removal the same record re-throws, re-logs and re-reports to Rollbar on
      // every read for the life of the device — the restore effect re-runs whenever the device
      // identity changes, so repeatedly, not once. Same argument as the expiry branch below.
      //
      // This inner catch covers BOTH unreadable shapes — a JSON.parse failure and a well-formed
      // object that is not our envelope. Only the second was handled at first, which left the
      // commoner one (a truncated write) re-reporting forever.
      //
      // Deliberately NOT merged into the outer catch: that one also fires when `getItem` itself
      // throws, and attempting a `removeItem` against storage that is already refusing access
      // would replace the real error with a second one.
      try {
        window.localStorage.removeItem(key);
      } catch {
        // Storage refused the cleanup. Nothing more to do here — the outer catch still reports
        // the original failure, which is the part that must not be swallowed.
      }
      throw unreadable;
    }

    const now = clock?.now ?? Date.now();
    if (now - parsed.savedAt > GUEST_FIELDS_TTL_MS) {
      // The read is the only moment this can be aged out — see the expiry invariant.
      window.localStorage.removeItem(key);
      return null;
    }

    const stored = parsed.values as Record<string, unknown>;
    const values: GuestFieldValues = {};
    for (const field of FIELD_KEYS) {
      const value = clean(stored[field]);
      if (value) values[field] = value;
    }

    return hasAnyValue(values) ? values : null;
  } catch (error) {
    logger.error('Could not read the saved guest field values', error, {
      feature: 'guest-field-memory',
      route: 'readGuestFields',
    });
    return null;
  }
}

/**
 * Merges `values` over whatever is already stored and stamps the write time.
 *
 * Only non-empty fields are written, so a flow that collects just a plate does not erase a
 * previously saved email.
 */
export function writeGuestFields(values: GuestFieldValues, clock?: GuestFieldsClock): void {
  if (typeof window === 'undefined') return;

  const key = GUEST_FIELDS_KEY;

  try {
    // Reading through the normal path means an EXPIRED record is dropped rather than merged
    // forward — otherwise a 90-day-old plate could ride along on a fresh write forever.
    const next: GuestFieldValues = { ...(readGuestFields(clock) ?? {}) };

    for (const field of FIELD_KEYS) {
      const value = clean(values[field]);
      if (value) next[field] = value;
    }

    if (!hasAnyValue(next)) return;

    const envelope: GuestFieldsEnvelope = { values: next, savedAt: clock?.now ?? Date.now() };
    window.localStorage.setItem(key, JSON.stringify(envelope));
  } catch (error) {
    logger.error('Could not save the guest field values', error, {
      feature: 'guest-field-memory',
      route: 'writeGuestFields',
    });
  }
}

/**
 * Removes every stored guest record on this origin.
 *
 * Still a prefix sweep rather than a single `removeItem` on {@link GUEST_FIELDS_KEY}, even though
 * current code writes only that one key: a browser carried over from before PB-5009 still holds
 * per-company-and-device records, and sign-in must clear those too rather than leaving guest PII
 * behind for a signed-in parker.
 */
export function clearAllGuestFields(): void {
  if (typeof window === 'undefined') return;

  try {
    const doomed: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(GUEST_FIELDS_KEY_PREFIX)) doomed.push(key);
    }

    // Collected first: removing while iterating shifts the indices underneath the loop.
    doomed.forEach((key) => window.localStorage.removeItem(key));
  } catch (error) {
    logger.error('Could not clear the saved guest field values', error, {
      feature: 'guest-field-memory',
      route: 'clearAllGuestFields',
    });
  }
}
